/**
 * DevToolsPanel — dev-environment-only testing shortcuts: grant money,
 * teleport a ship straight to a port, hire a crew member of any chosen
 * role/tier. See server/transport/intents/dev.js for the server-side
 * handlers and why these are gated on GAME_ENV=dev rather than isAdmin.
 *
 * Deliberately separate from src/ui/panels/AdminPanel.js (route waypoint
 * editor) and AdminEventsPanel.js (world event injection) — those are
 * isAdmin-gated operator tools; this is a dev-environment-gated testing
 * tool, a different axis entirely (see HUD.js's setDevControlsVisible /
 * _updateDevControls, which drives this panel's own HUD button the same
 * way admin tools drive theirs from a different flag).
 *
 * Every action here acts on the CALLING player's own state — same as
 * every other /api/game/* intent — there is no "act as a different
 * player" capability, dev environment or not.
 */
import { EventBus } from '../../core/EventBus.js';
import { GameState } from '../../core/GameState.js';
import { Modal } from '../components/Modal.js';
import { serverIntent } from '../../core/serverIntent.js';
import { formatMoney, formatDate } from '../../utils/formatting.js';

const ROLES = [
  { id: 'navigator',      label: 'Navigator' },
  { id: 'engineer',       label: 'Engineer' },
  { id: 'market_analyst', label: 'Market Analyst (fleet-wide, no ship)' }
];
const TIERS = ['junior', 'senior', 'expert'];

export class DevToolsPanel {
  constructor() {
    this.modal = new Modal('dev-tools-modal', { title: 'Dev Tools' });

    this._moneyAmount  = 100000;
    this._moveShipId   = null;
    this._movePortId   = null;
    this._hireRole     = 'navigator';
    this._hireTier     = 'senior';
    this._hireShipId   = null;
    this._busy         = false;

    EventBus.on('ui:devToolsOpen', () => this.open());
    // Ship/port lists can change while the panel is open (bought a ship,
    // one arrived elsewhere) — cheap to just re-render on the same events
    // the rest of the UI already reacts to.
    EventBus.on('ship:purchased', () => { if (this.modal.isOpen()) this._render(); });
    EventBus.on('ship:sold',      () => { if (this.modal.isOpen()) this._render(); });
    // The WS delta a dev/set-date intent triggers (pushWorldDateChanged())
    // arrives asynchronously, separate from that intent's own REST
    // response — this catches GameState.currentDate actually updating,
    // rather than relying on the _setDate() handler's own immediate
    // _render() call to have already seen it.
    EventBus.on('time:dayPassed', () => { if (this.modal.isOpen()) this._render(); });
  }

  open() {
    this._syncDefaultSelections();
    this._render();
    this.modal.open();
  }

  _syncDefaultSelections() {
    const ships = GameState.getAllShips();
    if (!ships.find(s => s.id === this._moveShipId)) this._moveShipId = ships[0]?.id || null;
    if (!ships.find(s => s.id === this._hireShipId)) this._hireShipId = ships[0]?.id || null;
    if (!this._movePortId) {
      const ports = GameState.getAllPorts();
      this._movePortId = ports[0]?.id || null;
    }
  }

  _render() {
    const ships = GameState.getAllShips();
    const ports = [...GameState.getAllPorts()].sort((a, b) => a.name.localeCompare(b.name));

    const shipOptions = (selectedId) => ships.map(s =>
      `<option value="${s.id}" ${s.id === selectedId ? 'selected' : ''}>${s.name} (${s.status})</option>`
    ).join('') || '<option value="">No ships</option>';

    const portOptions = ports.map(p =>
      `<option value="${p.id}" ${p.id === this._movePortId ? 'selected' : ''}>${p.name}, ${p.country}</option>`
    ).join('');

    this.modal.setBody(`
      <div class="dev-tools-panel">
        <section class="dev-tools-section">
          <h5>World Date</h5>
          <p class="dev-tools-hint">
            Current: ${formatDate(GameState.currentDate)}. A world left
            running at a fast speed drifts ahead of real time — jump it
            back whenever that's not what you want.
          </p>
          <div class="route-field">
            <label>Set to</label>
            <input type="datetime-local" id="dt-date-input" value="${this._dateInputValue()}">
          </div>
          <button class="btn btn-primary btn-sm" id="dt-date-set-btn" ${this._busy ? 'disabled' : ''}>
            Set Date
          </button>
          <button class="btn btn-sm" id="dt-date-now-btn" ${this._busy ? 'disabled' : ''}>
            Set to Now (real time)
          </button>
        </section>

        <section class="dev-tools-section">
          <h5>Add Money</h5>
          <div class="route-field">
            <label>Amount (negative to deduct)</label>
            <input type="number" id="dt-money-amount" step="1000" value="${this._moneyAmount}">
          </div>
          <button class="btn btn-primary btn-sm" id="dt-money-btn" ${this._busy ? 'disabled' : ''}>
            Add to Balance (current: ${formatMoney(GameState.player?.money || 0)})
          </button>
        </section>

        <section class="dev-tools-section">
          <h5>Teleport Ship</h5>
          <div class="route-field">
            <label>Ship</label>
            <select id="dt-move-ship">${shipOptions(this._moveShipId)}</select>
          </div>
          <div class="route-field">
            <label>Destination port</label>
            <select id="dt-move-port">${portOptions}</select>
          </div>
          <button class="btn btn-primary btn-sm" id="dt-move-btn" ${this._busy || !ships.length ? 'disabled' : ''}>
            Move Instantly
          </button>
        </section>

        <section class="dev-tools-section">
          <h5>Hire Crew (any role/tier, bypasses port pools)</h5>
          <div class="route-field">
            <label>Role</label>
            <select id="dt-hire-role">
              ${ROLES.map(r => `<option value="${r.id}" ${r.id === this._hireRole ? 'selected' : ''}>${r.label}</option>`).join('')}
            </select>
          </div>
          <div class="route-field">
            <label>Skill tier</label>
            <select id="dt-hire-tier">
              ${TIERS.map(t => `<option value="${t}" ${t === this._hireTier ? 'selected' : ''}>${t}</option>`).join('')}
            </select>
          </div>
          <div class="route-field" id="dt-hire-ship-field" ${this._hireRole === 'market_analyst' ? 'style="display:none"' : ''}>
            <label>Ship</label>
            <select id="dt-hire-ship">${shipOptions(this._hireShipId)}</select>
          </div>
          <button class="btn btn-primary btn-sm" id="dt-hire-btn" ${this._busy ? 'disabled' : ''}>
            Hire
          </button>
        </section>

        <section class="dev-tools-section">
          <h5>Sea Route Waypoints</h5>
          <p class="dev-tools-hint">
            Opens the map-overlay editor for the shared sea-waypoint network
            (drag to move, drag a connection line to insert, right-click to
            remove). Also reachable via Ctrl+Shift+A.
          </p>
          <button class="btn btn-primary btn-sm" id="dt-waypoints-btn">Open Waypoint Editor</button>
        </section>
      </div>`);

    this._bindEvents();
  }

  /** Current in-game date as a `datetime-local` input value (local time, no offset suffix). */
  _dateInputValue() {
    const d = GameState.currentDate instanceof Date ? GameState.currentDate : new Date(GameState.currentDate);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  _bindEvents() {
    document.getElementById('dt-date-set-btn').addEventListener('click', () => this._setDate(false));
    document.getElementById('dt-date-now-btn').addEventListener('click', () => this._setDate(true));

    document.getElementById('dt-money-amount').addEventListener('input', (e) => {
      this._moneyAmount = Number(e.target.value);
    });
    document.getElementById('dt-money-btn').addEventListener('click', () => this._addMoney());

    document.getElementById('dt-move-ship').addEventListener('change', (e) => { this._moveShipId = e.target.value; });
    document.getElementById('dt-move-port').addEventListener('change', (e) => { this._movePortId = e.target.value; });
    document.getElementById('dt-move-btn').addEventListener('click', () => this._moveShip());

    document.getElementById('dt-hire-role').addEventListener('change', (e) => {
      this._hireRole = e.target.value;
      this._render(); // toggles the ship field's visibility
    });
    document.getElementById('dt-hire-tier').addEventListener('change', (e) => { this._hireTier = e.target.value; });
    const hireShipSel = document.getElementById('dt-hire-ship');
    if (hireShipSel) hireShipSel.addEventListener('change', (e) => { this._hireShipId = e.target.value; });
    document.getElementById('dt-hire-btn').addEventListener('click', () => this._hireCrew());

    document.getElementById('dt-waypoints-btn').addEventListener('click', () => {
      // A full map-overlay editor doesn't make sense stacked underneath
      // this modal — close it first, same as any other "open a different
      // full-screen view" action elsewhere in the UI.
      this.modal.close();
      EventBus.emit('ui:adminPanelOpen');
    });
  }

  /**
   * @param {boolean} useNow  true for the "Set to Now" button — ignores
   *   whatever's in the date input and uses the browser's real clock.
   */
  async _setDate(useNow) {
    // `new Date(datetime-local value)` — a string like "2026-09-04T14:30"
    // with no timezone suffix — parses as local time per spec, matching
    // what the input actually shows the user.
    const date = useNow ? new Date() : new Date(document.getElementById('dt-date-input').value);
    if (Number.isNaN(date.getTime())) {
      EventBus.emit('ui:notification', { message: 'Invalid date', type: 'error' });
      return;
    }
    this._busy = true;
    const result = await serverIntent('dev/set-date', { date: date.toISOString() });
    this._busy = false;
    if (result.success) {
      EventBus.emit('ui:notification', {
        message: `World date set to ${formatDate(new Date(result.currentDate))}`,
        type: 'success'
      });
    }
    this._render();
  }

  async _addMoney() {
    if (!this._moneyAmount) return;
    this._busy = true;
    const result = await serverIntent('dev/add-money', { amount: this._moneyAmount });
    this._busy = false;
    if (result.success) {
      EventBus.emit('ui:notification', { message: `Balance updated: ${formatMoney(result.money)}`, type: 'success' });
    }
    this._render();
  }

  async _moveShip() {
    if (!this._moveShipId || !this._movePortId) return;
    this._busy = true;
    const result = await serverIntent('dev/move-ship', { shipId: this._moveShipId, portId: this._movePortId });
    this._busy = false;
    if (result.success) {
      const port = GameState.getPort(this._movePortId);
      EventBus.emit('ui:notification', { message: `Ship moved to ${port?.name || this._movePortId}`, type: 'success' });
    }
    this._render();
  }

  async _hireCrew() {
    if (this._hireRole !== 'market_analyst' && !this._hireShipId) {
      EventBus.emit('ui:notification', { message: 'No ship available to assign crew to.', type: 'error' });
      return;
    }
    this._busy = true;
    const result = await serverIntent('dev/hire-crew', {
      role:     this._hireRole,
      skillTier: this._hireTier,
      shipId:   this._hireRole === 'market_analyst' ? undefined : this._hireShipId
    });
    this._busy = false;
    if (result.success) {
      const name = result.crew?.name || result.analyst?.name || 'Crew member';
      EventBus.emit('ui:notification', { message: `Hired ${name} (${this._hireTier} ${this._hireRole})`, type: 'success' });
    }
    this._render();
  }
}
